import type {
  AccessibilityInfo,
  InnovationSection,
  OpeningHours,
  Translations,
} from '@explorentia/shared';

export const API_BASE_URL =
  import.meta.env.VITE_API_URL ?? 'https://api.explorentia.tech.anroit.com';

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

// Turn a thrown error into a message fit for the UI. The API sends a
// `{ message }` body for the cases it wants surfaced (e.g. a duplicate Short
// ID → 409); prefer that over the raw "API 409" status string.
export function errorMessage(err: unknown, fallback = 'Something went wrong — please try again.'): string {
  if (err instanceof ApiError) {
    const body = err.body as { message?: string } | null;
    if (body?.message) return body.message;
    return `${fallback} (API ${err.status})`;
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

// Build a `?k=v` query string from a plain object, dropping empty values.
// Centralised here so every analytics call uses the same encoding —
// Drizzle's date filters expect ISO strings and a missing query param is
// not the same as an empty string (the latter would 400 on Zod validation).
// Accept any object — keeps DateRange and its extensions happy without
// forcing every caller to declare an index signature. Values are
// stringified; undefined / null / empty string get skipped.
function qs(args: object): string {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(args as Record<string, unknown>)) {
    if (v === undefined || v === null || v === '') continue;
    params.set(k, String(v));
  }
  const out = params.toString();
  return out ? `?${out}` : '';
}

export async function apiFetch<T = unknown>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
  });
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, `API ${res.status} ${res.statusText}`, body);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// ---------------------------------------------------------------------------
// Shared enums mirroring the pgEnum values
// ---------------------------------------------------------------------------

export type EntityStatus = 'active' | 'inactive' | 'archived';
export type PoiCategory =
  | 'church'
  | 'monastery'
  | 'museum'
  | 'business'
  | 'restaurant'
  | 'winery'
  | 'local_shop'
  | 'cultural_landmark'
  | 'viewpoint'
  | 'other';
export type BadgeUnlockCondition = 'quiz_correct' | 'visit_only';
export type PerkCondition = 'poi_visit' | 'quiz_correct' | 'route_completed';
export type QrTargetKind = 'location' | 'route';

export const POI_CATEGORIES: PoiCategory[] = [
  'church',
  'monastery',
  'museum',
  'business',
  'restaurant',
  'winery',
  'local_shop',
  'cultural_landmark',
  'viewpoint',
  'other',
];

export const ENTITY_STATUSES: EntityStatus[] = ['active', 'inactive', 'archived'];

// Human-readable labels for the enums above — keeps admin UI consistent
// without sprinkling switch statements through every page.
export const POI_CATEGORY_LABELS: Record<PoiCategory, string> = {
  church: 'Church',
  monastery: 'Monastery',
  museum: 'Museum',
  business: 'Business (generic)',
  restaurant: 'Restaurant',
  winery: 'Winery',
  local_shop: 'Local shop',
  cultural_landmark: 'Cultural landmark',
  viewpoint: 'Viewpoint',
  other: 'Other',
};
export const ENTITY_STATUS_LABELS: Record<EntityStatus, string> = {
  active: 'Active',
  inactive: 'Inactive',
  archived: 'Archived',
};

// ---------------------------------------------------------------------------
// Domain types
// ---------------------------------------------------------------------------

export interface AdminUser {
  sub: string;
  email: string;
  name?: string;
  picture?: string;
}

export interface AdminVillage {
  id: string;
  slug: string;
  name: Translations;
  description: Translations;
  /** Long-form text on the public community page. */
  story: Translations | null;
  heroImageUrl: string | null;
  lat: number;
  lng: number;
  createdAt: string;
  updatedAt: string;
}

// Platform-wide settings (one row server-side). Today this is the mandatory
// GDPR notice shown on POI #1 of every route before the demographics form —
// global rather than per-route so the client edits the legal text once and no
// route can end up without it.
export interface AdminAppSettings {
  id: string;
  /** The public site's Innovation & Technology block (Essentials item 8). */
  innovationSection: InnovationSection | null;
  gdprNoticeTitle: Translations | null;
  gdprNoticeBody: Translations | null;
  privacyPolicyUrl: string | null;
  cookiePolicyUrl: string | null;
  legalComplianceUrl: string | null;
  couponTermsUrl: string | null;
  updatedAt: string;
}

export interface AdminRoute {
  id: string;
  villageId: string;
  name: Translations;
  // Client iteration 2 — end-of-route content. All nullable: a route without
  // them falls back to the visitor app's built-in copy.
  bigRewardTitle: Translations | null;
  bigRewardDescription: Translations | null;
  bigRewardImageUrl: string | null;
  villagesLinkUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

// "Worth Visiting" recommendation shown at the end of a route. Not a POI:
// no QR, no quiz, no badge, no effect on progression.
export interface AdminRoutePlace {
  id: string;
  routeId: string;
  position: number;
  title: Translations;
  description: Translations | null;
  imageUrl: string | null;
  address: string | null;
  lat: number | null;
  lng: number | null;
  mapUrl: string | null;
  status: EntityStatus;
  createdAt: string;
  updatedAt: string;
}

export const EXPERIENCE_CATEGORIES = [
  'nature',
  'culture',
  'gastronomy',
  'crafts',
  'heritage',
  'trails',
] as const;
export type ExperienceCategory = (typeof EXPERIENCE_CATEGORIES)[number];

export interface AdminExperience {
  id: string;
  villageId: string;
  position: number;
  category: ExperienceCategory;
  title: Translations;
  description: Translations;
  durationMinutes: number | null;
  imageUrl: string | null;
  bookingUrl: string | null;
  status: EntityStatus;
  createdAt: string;
  updatedAt: string;
}

export interface AdminVillageImage {
  id: string;
  villageId: string;
  position: number;
  url: string;
  alt: Translations;
  createdAt?: string;
}

export interface AdminLocation {
  id: string;
  routeId: string;
  shortId: string;
  /** URL segment on the public site: /villages/<village>/<slug>/. */
  slug: string | null;
  position: number;
  category: PoiCategory;
  status: EntityStatus;
  isRequired: boolean;
  address: string | null;
  title: Translations;
  subtitle: Translations;
  story: Translations;
  // Legacy single blob, still returned for back-compat. `practicalTipsItems`
  // is what the editor and the visitor app use.
  practicalTips: Translations | null;
  practicalTipsItems: Translations<string[]> | null;
  openingHours: OpeningHours | null;
  accessibility: AccessibilityInfo | null;
  lat: number;
  lng: number;
  videoUrl: string | null;
  videoPoster: string | null;
  // Legacy embedded badge/perk fields — still in the response during the M1
  // transition. Day 6 will drop them; consumers should treat them as
  // potentially-missing read-only mirrors of the dedicated tables.
  badgeImageUrl: string;
  badgeTitle: Translations;
  perkTitle: Translations | null;
  perkDescription: Translations | null;
  perkPartnerName: string | null;
  perkMapUrl: string | null;
  perkImageUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AdminLocationImage {
  id: string;
  locationId: string;
  role: 'hero' | 'gallery';
  position: number;
  url: string;
  alt: Translations;
}

export interface AdminBusinessImage {
  id: string;
  businessId: string;
  role: 'hero' | 'gallery';
  position: number;
  url: string;
  alt: Translations;
}

export interface AdminQuizQuestion {
  id: string;
  locationId: string;
  position: number;
  question: Translations;
  options: Translations<string[]>;
  correctIndex: number;
}

export interface AdminQrCode {
  id: string;
  code: string;
  name: string;
  targetKind: QrTargetKind;
  locationId: string | null;
  routeId: string | null;
  status: EntityStatus;
  internalNotes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AdminQrCodeStats {
  scans: number;
}

export interface AdminBusiness {
  id: string;
  villageId: string | null;
  name: string;
  /** URL segment under the village: /villages/<village>/businesses/<slug>/. */
  slug: string | null;
  description: Translations | null;
  code: string;
  contactEmail: string | null;
  phone: string | null;
  websiteUrl: string | null;
  bookingUrl: string | null;
  address: string | null;
  mapUrl: string | null;
  lat: number | null;
  lng: number | null;
  openingHours: OpeningHours | null;
  logoUrl: string | null;
  /** YouTube / Vimeo links played on the public business page, in order. */
  videoUrls: string[];
  /** Internal — never rendered on the public site. */
  notes: string | null;
  isPublic: boolean;
  status: EntityStatus;
  createdAt: string;
  updatedAt: string;
}

export interface AdminBadge {
  id: string;
  locationId: string;
  title: Translations;
  description: Translations | null;
  imageUrl: string;
  unlockCondition: BadgeUnlockCondition;
  status: EntityStatus;
  createdAt: string;
  updatedAt: string;
}

export interface AdminPerk {
  id: string;
  locationId: string;
  businessId: string | null;
  title: Translations;
  description: Translations;
  imageUrl: string | null;
  condition: PerkCondition;
  tokenTtlMinutes: number;
  status: EntityStatus;
  createdAt: string;
  updatedAt: string;
}

export type RedemptionStatus = 'issued' | 'redeemed' | 'expired';

export const REDEMPTION_STATUSES: RedemptionStatus[] = ['issued', 'redeemed', 'expired'];

export const REDEMPTION_STATUS_LABELS: Record<RedemptionStatus, string> = {
  issued: 'Issued',
  redeemed: 'Redeemed',
  expired: 'Expired',
};

export interface AdminRedemption {
  id: string;
  sessionId: string;
  perkId: string;
  businessId: string | null;
  locationId: string | null;
  routeId: string | null;
  token: string;
  humanCode: string;
  status: RedemptionStatus;
  issuedAt: string;
  expiresAt: string;
  redeemedAt: string | null;
  // numeric(10,2) in Postgres comes back as a string on the wire — we keep
  // it as such here and parse on the edit form. Avoids float imprecision
  // when round-tripping bills like 12.99.
  totalBill: string | null;
  // Scan counters — how often the coupon was opened, independent of whether
  // a business ever confirmed it.
  scanCount: number;
  firstScannedAt: string | null;
  lastScannedAt: string | null;
  createdAt: string;
  updatedAt: string;
  perk: {
    id: string;
    title: Translations;
    description: Translations;
    condition: PerkCondition;
  } | null;
  business: { id: string; name: string; code: string } | null;
  location: { id: string; title: Translations; shortId: string } | null;
}

export interface AdminRedemptionsSummary {
  issued: { count: number; totalBillSum: string | null };
  redeemed: { count: number; totalBillSum: string | null };
  expired: { count: number; totalBillSum: string | null };
}

// ---------------------------------------------------------------------------
// Module 10 analytics types
// ---------------------------------------------------------------------------

export interface AdminAnalyticsOverview {
  // `total` is unique visitors (distinct devices), not session rows: since
  // EXP-11 a session only exists once the visitor completes the form, and
  // "visitors" must mean everyone who turned up. `withProfile` is the subset
  // that filled it in.
  sessions: {
    total: number;
    completed: number;
    withProfile: number;
    // Visitors who earned at least one badge — the stage between filling in
    // the form and finishing the route. Nested inside `withProfile`.
    withBadge: number;
    // Visitors who earned a badge before the form became mandatory (2 Sep).
    // Outside the funnel; shown as a footnote so the history is visible.
    withBadgeBeforeForm: number;
    completionRate: number;
  };
  // Real visitor QR scans at the POIs, reported by the mobile app. Build-time
  // prerender fetches are excluded server-side.
  // `poi` counts scan events, `poiDevices` the distinct devices behind them.
  // One visitor re-opening a sticker four times is four scans, one device.
  scans: { poi: number; poiDevices: number };
  redemptions: {
    issued: number;
    redeemed: number;
    expired: number;
    // Coupon scans, reported separately from redemptions: opening a coupon
    // is not the same as a business honouring it.
    scans: number;
    scanned: number;
    conversionRate: number;
    totalBillSum: string | null;
  };
  ratings: { total: number; average: number | null };
  suggestions: { total: number };
}

export interface AdminDemographicsBucket {
  value: string | null;
  count: number;
}
export interface AdminAnalyticsDemographics {
  nationality: AdminDemographicsBucket[];
  ageRange: AdminDemographicsBucket[];
  gender: AdminDemographicsBucket[];
  language: AdminDemographicsBucket[];
  // Module 9 "How did you hear about us?" — slugs map to HEARD_FROM_LABELS.
  heardFrom: AdminDemographicsBucket[];
  // The breakdowns above count only visitors who completed the form, so the
  // dashboard states how much of the window they speak for.
  coverage: { sessions: number; withProfile: number };
}

// Human labels for the final-POI marketing question slugs. Keep in sync with
// `heardFromValues` in apps/api/src/routes/feedback.ts and the mobile
// `feedback.heardFromOptions` translations.
export const HEARD_FROM_LABELS: Record<string, string> = {
  social_media: 'Social media',
  radio: 'Radio advertisement',
  word_of_mouth: 'From a friend / word of mouth',
  hotel_agency: 'Hotel / tourist agency',
  just_visiting: 'Just visiting the village',
};

export interface AdminFunnelLocation {
  id: string;
  position: number;
  title: Translations;
  // Everyone who reached this POI, however they got there.
  visitors: number;
  // Of the visitors who started at point 1, how many reached this POI. This
  // is the number that behaves like a funnel; `visitors` does not, because
  // people arrive mid-route.
  sequential: number;
}
export interface AdminAnalyticsFunnel {
  routes: {
    id: string;
    name: Translations;
    villageName: Translations | null;
    startedAtFirst: number;
    locations: AdminFunnelLocation[];
  }[];
}

export interface AdminAnalyticsRatingRow {
  locationId: string | null;
  // `position` and `villageName` ride along because the table is sorted by
  // volume, not by route: "RELIGIOUS HERITAGE POINT" exists in three villages,
  // and without them the row doesn't say which point of which route it is.
  location: {
    id: string;
    title: Translations;
    shortId: string;
    position: number;
    villageName: Translations | null;
  } | null;
  count: number;
  average: number | null;
  distribution: Record<'1' | '2' | '3' | '4' | '5', number>;
}

export interface AdminAnalyticsSuggestion {
  id: string;
  sessionId: string;
  locationId: string | null;
  text: string;
  createdAt: string;
  location: { id: string; title: Translations; shortId: string } | null;
}

export interface AdminAnalyticsRedemptionsSummaryByBusiness {
  businessId: string | null;
  businessName: string | null;
  issued: number;
  redeemed: number;
  expired: number;
  scans: number;
  totalBillSum: string | null;
}
export interface AdminAnalyticsRedemptionsSummaryByPerk {
  perkId: string;
  perkTitle: Translations | null;
  locationId: string | null;
  issued: number;
  redeemed: number;
  expired: number;
  scans: number;
}

interface DateRange {
  from?: string;
  to?: string;
}

// Every dashboard call can be narrowed to one village. Omitted means "all
// villages", which is what the dashboard showed unconditionally before.
interface DashboardFilter extends DateRange {
  villageId?: string;
}

// Mirrors `PublishStatus` in apps/api/src/lib/github.ts.
export interface PublishStatus {
  running: { startedAt: string } | null;
  last: { result: 'success' | 'failure'; finishedAt: string } | null;
}

// ---------------------------------------------------------------------------
// API surface
// ---------------------------------------------------------------------------

export const api = {
  me: () => apiFetch<{ user: AdminUser | null }>('/auth/me'),
  logout: () => apiFetch<{ ok: true }>('/auth/logout', { method: 'POST' }),
  // Triggers a mobile static-site rebuild so CMS content goes live. The
  // rebuild runs in CI (a few minutes); 409 while one is already running.
  publish: () => apiFetch<{ ok: true; ref: string }>('/admin/publish', { method: 'POST' }),
  publishStatus: () => apiFetch<PublishStatus>('/admin/publish'),
  // Translates a batch of English strings into other locales via OpenAI.
  // Response arrays are aligned to the input `texts` order.
  translate: (body: { texts: string[]; targetLocales?: string[]; context?: string }) =>
    apiFetch<{ translations: Record<string, string[]> }>('/admin/translate', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // ---- Platform settings (GDPR notice)
  getSettings: () => apiFetch<{ settings: AdminAppSettings }>('/admin/settings'),
  updateSettings: (
    body: Partial<Omit<AdminAppSettings, 'id' | 'updatedAt'>>,
  ) =>
    apiFetch<{ settings: AdminAppSettings }>('/admin/settings', {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  // ---- Villages
  listVillages: () => apiFetch<{ villages: AdminVillage[] }>('/admin/villages'),
  getVillage: (id: string) => apiFetch<{ village: AdminVillage }>(`/admin/villages/${id}`),
  updateVillage: (id: string, body: Partial<AdminVillage>) =>
    apiFetch<{ village: AdminVillage }>(`/admin/villages/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  createVillage: (body: Partial<AdminVillage>) =>
    apiFetch<{ village: AdminVillage }>('/admin/villages', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // ---- Routes
  listRoutes: (villageId: string) =>
    apiFetch<{ routes: AdminRoute[] }>(`/admin/villages/${villageId}/routes`),
  getRoute: (id: string) =>
    apiFetch<{ route: AdminRoute }>(`/admin/routes/${id}`),
  createRoute: (body: { villageId: string; name: Translations }) =>
    apiFetch<{ route: AdminRoute }>('/admin/routes', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateRoute: (
    id: string,
    body: Partial<
      Pick<
        AdminRoute,
        | 'name'
        | 'bigRewardTitle'
        | 'bigRewardDescription'
        | 'bigRewardImageUrl'
        | 'villagesLinkUrl'
      >
    >,
  ) =>
    apiFetch<{ route: AdminRoute }>(`/admin/routes/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  // Hard delete — cascades to every POI in the route. Returns how many POIs
  // went with it so the UI can confirm the outcome.
  deleteRoute: (id: string) =>
    apiFetch<{ ok: true; deletedLocations: number }>(`/admin/routes/${id}`, {
      method: 'DELETE',
    }),

  // ---- Worth Visiting places
  listRoutePlaces: (routeId: string) =>
    apiFetch<{ places: AdminRoutePlace[] }>(`/admin/routes/${routeId}/places`),
  createRoutePlace: (
    body: Omit<AdminRoutePlace, 'id' | 'createdAt' | 'updatedAt'>,
  ) =>
    apiFetch<{ place: AdminRoutePlace }>('/admin/route-places', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateRoutePlace: (id: string, body: Partial<AdminRoutePlace>) =>
    apiFetch<{ place: AdminRoutePlace }>(`/admin/route-places/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteRoutePlace: (id: string) =>
    apiFetch<{ ok: true }>(`/admin/route-places/${id}`, { method: 'DELETE' }),

  // ---- Locations
  listLocations: (routeId: string) =>
    apiFetch<{ locations: AdminLocation[] }>(`/admin/routes/${routeId}/locations`),
  createLocation: (
    body: Pick<
      AdminLocation,
      'shortId' | 'position' | 'title' | 'subtitle' | 'story' | 'lat' | 'lng'
    > & {
      routeId: string;
      category?: AdminLocation['category'];
      isRequired?: boolean;
      status?: EntityStatus;
      address?: string | null;
    },
  ) =>
    apiFetch<{ location: AdminLocation }>('/admin/locations', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  getLocation: (id: string) =>
    apiFetch<{
      location: AdminLocation;
      images: AdminLocationImage[];
      quiz: AdminQuizQuestion[];
    }>(`/admin/locations/${id}`),
  updateLocation: (id: string, body: Partial<AdminLocation>) =>
    apiFetch<{ location: AdminLocation }>(`/admin/locations/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  // ---- QR codes
  listQrCodes: (filters?: { locationId?: string; routeId?: string; status?: EntityStatus }) => {
    const qs = new URLSearchParams();
    if (filters?.locationId) qs.set('locationId', filters.locationId);
    if (filters?.routeId) qs.set('routeId', filters.routeId);
    if (filters?.status) qs.set('status', filters.status);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return apiFetch<{ qrCodes: AdminQrCode[] }>(`/admin/qr-codes${suffix}`);
  },
  createQrCode: (body: Omit<AdminQrCode, 'id' | 'createdAt' | 'updatedAt'>) =>
    apiFetch<{ qrCode: AdminQrCode }>('/admin/qr-codes', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  getQrCode: (id: string) =>
    apiFetch<{ qrCode: AdminQrCode; stats: AdminQrCodeStats }>(`/admin/qr-codes/${id}`),
  updateQrCode: (id: string, body: Partial<AdminQrCode>) =>
    apiFetch<{ qrCode: AdminQrCode }>(`/admin/qr-codes/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  archiveQrCode: (id: string) =>
    apiFetch<{ qrCode: AdminQrCode }>(`/admin/qr-codes/${id}`, { method: 'DELETE' }),

  // ---- Businesses
  listBusinesses: () => apiFetch<{ businesses: AdminBusiness[] }>('/admin/businesses'),
  // Only the name is required. Left out, the code is generated from it; the public
  // profile — description, hours, booking link — is filled in afterwards on
  // the business page, so everything else is optional here.
  createBusiness: (
    body: Partial<Omit<AdminBusiness, 'id' | 'createdAt' | 'updatedAt'>> & {
      name: string;
      code?: string;
    },
  ) =>
    apiFetch<{ business: AdminBusiness }>('/admin/businesses', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  getBusiness: (id: string) =>
    apiFetch<{ business: AdminBusiness }>(`/admin/businesses/${id}`),
  updateBusiness: (id: string, body: Partial<AdminBusiness>) =>
    apiFetch<{ business: AdminBusiness }>(`/admin/businesses/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  // ---- Badges
  getLocationBadge: (locationId: string) =>
    apiFetch<{ badge: AdminBadge | null }>(`/admin/locations/${locationId}/badge`),
  createBadge: (body: Omit<AdminBadge, 'id' | 'createdAt' | 'updatedAt'>) =>
    apiFetch<{ badge: AdminBadge }>('/admin/badges', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateBadge: (id: string, body: Partial<AdminBadge>) =>
    apiFetch<{ badge: AdminBadge }>(`/admin/badges/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  // ---- Quiz questions
  listQuiz: (locationId: string) =>
    apiFetch<{ quiz: AdminQuizQuestion[] }>(`/admin/locations/${locationId}/quiz`),
  createQuizQuestion: (body: Omit<AdminQuizQuestion, 'id'>) =>
    apiFetch<{ question: AdminQuizQuestion }>('/admin/quiz-questions', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateQuizQuestion: (id: string, body: Partial<AdminQuizQuestion>) =>
    apiFetch<{ question: AdminQuizQuestion }>(`/admin/quiz-questions/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteQuizQuestion: (id: string) =>
    apiFetch<{ ok: true }>(`/admin/quiz-questions/${id}`, { method: 'DELETE' }),

  // ---- Experiences
  listExperiences: (villageId: string) =>
    apiFetch<{ experiences: AdminExperience[] }>(`/admin/villages/${villageId}/experiences`),
  createExperience: (body: Omit<AdminExperience, 'id' | 'createdAt' | 'updatedAt' | 'status'>) =>
    apiFetch<{ experience: AdminExperience }>('/admin/experiences', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateExperience: (id: string, body: Partial<AdminExperience>) =>
    apiFetch<{ experience: AdminExperience }>(`/admin/experiences/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteExperience: (id: string) =>
    apiFetch<{ ok: true }>(`/admin/experiences/${id}`, { method: 'DELETE' }),

  // ---- Village images
  listVillageImages: (villageId: string) =>
    apiFetch<{ images: AdminVillageImage[] }>(`/admin/villages/${villageId}/images`),
  createVillageImage: (body: Omit<AdminVillageImage, 'id' | 'createdAt'>) =>
    apiFetch<{ image: AdminVillageImage }>('/admin/village-images', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateVillageImage: (id: string, body: Partial<AdminVillageImage>) =>
    apiFetch<{ image: AdminVillageImage }>(`/admin/village-images/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteVillageImage: (id: string) =>
    apiFetch<{ ok: true }>(`/admin/village-images/${id}`, { method: 'DELETE' }),

  // ---- Location images
  listLocationImages: (locationId: string) =>
    apiFetch<{ images: AdminLocationImage[] }>(`/admin/locations/${locationId}/images`),
  createLocationImage: (body: Omit<AdminLocationImage, 'id'>) =>
    apiFetch<{ image: AdminLocationImage }>('/admin/location-images', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateLocationImage: (id: string, body: Partial<AdminLocationImage>) =>
    apiFetch<{ image: AdminLocationImage }>(`/admin/location-images/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteLocationImage: (id: string) =>
    apiFetch<{ ok: true }>(`/admin/location-images/${id}`, { method: 'DELETE' }),

  // ---- Business images
  listBusinessImages: (businessId: string) =>
    apiFetch<{ images: AdminBusinessImage[] }>(`/admin/businesses/${businessId}/images`),
  createBusinessImage: (body: Omit<AdminBusinessImage, 'id'>) =>
    apiFetch<{ image: AdminBusinessImage }>('/admin/business-images', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateBusinessImage: (id: string, body: Partial<AdminBusinessImage>) =>
    apiFetch<{ image: AdminBusinessImage }>(`/admin/business-images/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteBusinessImage: (id: string) =>
    apiFetch<{ ok: true }>(`/admin/business-images/${id}`, { method: 'DELETE' }),

  // ---- Perks
  listLocationPerks: (locationId: string) =>
    apiFetch<{ perks: AdminPerk[] }>(`/admin/locations/${locationId}/perks`),
  createPerk: (body: Omit<AdminPerk, 'id' | 'createdAt' | 'updatedAt'>) =>
    apiFetch<{ perk: AdminPerk }>('/admin/perks', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updatePerk: (id: string, body: Partial<AdminPerk>) =>
    apiFetch<{ perk: AdminPerk }>(`/admin/perks/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  // ---- Redemptions
  listRedemptions: (filters?: {
    status?: RedemptionStatus;
    businessId?: string;
    locationId?: string;
    routeId?: string;
    perkId?: string;
    issuedFrom?: string;
    issuedTo?: string;
    limit?: number;
    offset?: number;
  }) => {
    const qs = new URLSearchParams();
    if (filters) {
      for (const [k, v] of Object.entries(filters)) {
        if (v !== undefined && v !== '' && v !== null) qs.set(k, String(v));
      }
    }
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return apiFetch<{
      redemptions: AdminRedemption[];
      total: number;
      summary: AdminRedemptionsSummary;
    }>(`/admin/redemptions${suffix}`);
  },
  updateRedemption: (id: string, body: { totalBill: number | null }) =>
    apiFetch<{ redemption: AdminRedemption }>(`/admin/redemptions/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  // ---- Analytics
  // All five accept the same {from, to} ISO datetime window. We funnel
  // the date filter through a single helper so the call sites stay terse.
  analyticsOverview: (range: DashboardFilter = {}) =>
    apiFetch<AdminAnalyticsOverview>(`/admin/analytics/overview${qs(range)}`),
  analyticsDemographics: (range: DashboardFilter = {}) =>
    apiFetch<AdminAnalyticsDemographics>(`/admin/analytics/demographics${qs(range)}`),
  analyticsFunnel: (range: DashboardFilter = {}) =>
    apiFetch<AdminAnalyticsFunnel>(`/admin/analytics/funnel${qs(range)}`),
  analyticsRatings: (range: DashboardFilter = {}) =>
    apiFetch<{ ratings: AdminAnalyticsRatingRow[] }>(
      `/admin/analytics/ratings${qs(range)}`,
    ),
  analyticsSuggestions: (
    args: DashboardFilter & { locationId?: string; limit?: number; offset?: number } = {},
  ) =>
    apiFetch<{ suggestions: AdminAnalyticsSuggestion[]; total: number }>(
      `/admin/analytics/suggestions${qs(args)}`,
    ),
  analyticsRedemptionsSummary: (range: DashboardFilter = {}) =>
    apiFetch<{
      byBusiness: AdminAnalyticsRedemptionsSummaryByBusiness[];
      byPerk: AdminAnalyticsRedemptionsSummaryByPerk[];
    }>(`/admin/analytics/redemptions-summary${qs(range)}`),

  // CSV exports — return the absolute URL so the admin UI can render
  // an `<a href="...">` for the download instead of round-tripping the
  // bytes through React state.
  // The entry funnel, computed per village rather than for the current
  // filter — comparing villages is the point of the file.
  entryFunnelCsvUrl: (args: DashboardFilter = {}): string =>
    `${API_BASE_URL}/admin/exports/entry-funnel.csv${qs(args)}`,

  // The Route progress table as CSV — reach per POI plus the in-order funnel,
  // scoped by the same village and date filter as the dashboard section.
  routeProgressCsvUrl: (args: DashboardFilter = {}): string =>
    `${API_BASE_URL}/admin/exports/route-progress.csv${qs(args)}`,

  // "Export Village" — the complete current text of a village in every
  // locale. `md` reads well for proofreading, `json` round-trips for backup.
  villageExportUrl: (villageId: string, format: 'md' | 'json' = 'md'): string =>
    `${API_BASE_URL}/admin/villages/${villageId}/export?format=${format}`,

  csvUrl: (
    kind: 'redemptions' | 'ratings' | 'suggestions',
    range: DateRange = {},
  ): string => `${API_BASE_URL}/admin/exports/${kind}.csv${qs(range)}`,

  // Per-village demographic statistics (EXP-9). Without `villageId` the file
  // covers every village, one block each; with it, a single village — which is
  // how the button on the village page uses it.
  demographicsCsvUrl: (
    args: DateRange & { villageId?: string } = {},
  ): string => `${API_BASE_URL}/admin/exports/demographics.csv${qs(args)}`,

  // ---- Media uploads
  // Two-step flow: this call returns a short-lived presigned PUT URL and the
  // final publicly-readable URL. The component then PUTs the file directly
  // to S3 — the file never travels through our Lambda.
  requestUploadUrl: (body: {
    resourceType: 'locations' | 'badges' | 'perks' | 'businesses' | 'routes' | 'places';
    contentType: string;
    extension?: string;
  }) =>
    apiFetch<{
      uploadUrl: string;
      publicUrl: string;
      key: string;
      contentType: string;
      expiresInSeconds: number;
    }>('/admin/uploads/presign', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
};

// Upload a File directly to S3 via the presigned URL returned by
// `api.requestUploadUrl`. Lives next to the api object rather than inside
// it because it doesn't go through `apiFetch` — it talks to S3 instead.
export async function uploadFileToS3(
  uploadUrl: string,
  file: Blob,
  contentType: string,
): Promise<void> {
  const res = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    body: file,
  });
  if (!res.ok) {
    throw new Error(`S3 PUT failed with ${res.status}: ${await res.text().catch(() => '')}`);
  }
}
